import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Star, Download } from 'lucide-react';
import type { Movie } from '@/lib/mockData';

interface MovieCardProps {
  movie: Movie;
  href?: string;
}

export default function MovieCard({ movie, href = `/movie-detail-page` }: MovieCardProps) {
  return (
    <Link href={href} className="block group">
      <div className="glass-card glass-card-hover rounded-xl overflow-hidden cursor-pointer">
        {/* Poster */}
        <div className="relative aspect-[2/3] overflow-hidden">
          <AppImage
            src={movie.poster_url}
            alt={`${movie.title} poster — ${movie.genre} film from ${movie.year}`}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-80" />
          <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="flex items-center gap-1.5 px-4 py-2 neon-btn-solid-cyan rounded-full text-xs font-bold">
              <Download size={13} />
              Download
            </div>
          </div>
          {/* Rating badge */}
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
            <Star size={11} className="rating-star fill-yellow-400" />
            <span className="text-xs font-bold font-mono-data text-foreground">{movie.imdb_rating != null ? movie.imdb_rating.toFixed(1) : 'N/A'}</span>
          </div>
          {/* Featured badge */}
          {movie.featured && (
            <div className="absolute top-2 left-2">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent/80 text-white">FEATURED</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors duration-200">
            {movie.title}
          </h3>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-muted-foreground font-mono-data">{movie.year}</span>
            <span className="genre-badge">{movie.genre}</span>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <Download size={11} className="text-primary" />
            <span className="download-badge">{movie.download_count != null ? (movie.download_count / 1000).toFixed(1) + 'K' : '0K'}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}